import React, { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from 'react-query';
import uniqueId from 'lodash/uniqueId';
import cloneDeep from 'lodash/cloneDeep';
import { SuiSpinner } from '@splashthat/sui';

import { fetchConfirmationConfigData } from 'lib/SplashCakeClient/subclients/ConfirmationsCakeClient';
import { getLibrarianApiClient } from 'lib/SplashApiClient/subclients/LibrarianApiClient';
import { getFormBuilderApiClient } from 'lib/SplashApiClient/subclients/FormBuilderApiClient';
import { getDoubleOptInApiClient } from 'lib/SplashApiClient/subclients/DoubleOptInApiClient';

import { useNotificationManager, NOTIFICATION_STATUSES } from 'common/compounds/NotificationManager';
import { ModalManager } from 'common/components/TouchpointEditor/contexts/ModalManager';
import { TouchpointDataProvider } from 'common/components/TouchpointEditor/contexts/TouchpointDataContext';
import {
    LoadingSpinnerContainer,
    SmoothPropertyAnimation,
} from 'common/components/TouchpointEditor/components/TouchpointContainer';

import { CONFIRMATIONS } from 'common/components/TouchpointEditor/constants/touchpoints';
import { useEvent } from 'common/store/events';
import { useColors } from 'common/entities/color';
import { useFonts } from 'common/entities/font';
import { useLogos } from 'common/entities/logo';
import { useRootDomain, useFlags } from 'utils/react-context';

import { RSVP_YES } from '../ConfirmationsWorkspace/constants/triggerTypes';
import { AGENDA_BUTTON_META } from './hooks/useAddElementsByUrl';
import { confirmationsDefaultStructures } from './constants/defaultStructures';
import {
    createDOIMessageNode,
    createSessionSummaryNode,
    RSVP_YES_CONFIRMATION_MESSAGE_DISPLAY_NAME,
    RSVP_YES_DOI_MESSAGE_DISPLAY_NAME,
} from './constants/defaultStructures/rsvpYes';
import GenericConfirmation from './GenericConfirmation.container';

const extractTriggerInformationRegex =
    /^(rsvp-yes|rsvp-no|rsvp-update|rsvp-waitlist|double-opt-in|ticket-default|ticket-multi|ticket-custom)/;
const byId = (a, b) => a.id - b.id;

const ERROR_MESSAGE_LOAD = 'Error loading confirmation settings. Please try again.';
const ENABLE_SESSIONS_FORM_MESSAGE = 'Enable session registration in the form to display this element.';
const MULTI_SESSION_VERSION_2 = '2';

// Function to add Double Opt-In message component
export const addDoubleOptInMessage = (components) => {
    const updatedComponents = { ...components };
    const doubleOptInMessageChild = createDOIMessageNode();
    updatedComponents[doubleOptInMessageChild.id] = doubleOptInMessageChild;

    // Find the confirmation message component
    const confirmationMessageKey = Object.keys(updatedComponents).find(
        (key) => updatedComponents[key].props.meta.displayName === RSVP_YES_CONFIRMATION_MESSAGE_DISPLAY_NAME,
    );

    // Find the modal component
    const modalKey = Object.keys(updatedComponents).find((key) => updatedComponents[key].type === 'Modal');

    if (modalKey) {
        const modalComponent = updatedComponents[modalKey];
        const { childNodes } = modalComponent;
        if (Array.isArray(childNodes) && !childNodes.includes(doubleOptInMessageChild.id)) {
            // Insert the DOI message into the modal's child nodes at the desired position
            // (after the confirmation message if it exists, otherwise at the end)
            const confirmationMessageIndex = childNodes.indexOf(confirmationMessageKey);
            const insertIndex = confirmationMessageIndex > -1 ? confirmationMessageIndex + 1 : childNodes.length;

            const newChildNodes = [
                ...childNodes.slice(0, insertIndex),
                doubleOptInMessageChild.id,
                ...childNodes.slice(insertIndex),
            ];

            updatedComponents[modalKey] = {
                ...modalComponent,
                childNodes: newChildNodes,
            };
        }
    }

    return updatedComponents;
};

export default function ConfirmationsTouchpoint({ confirmationsState, goToConfirmationsSettings }) {
    const [, rerender] = useState(null);
    const rootDomain = useRootDomain();
    const event = useEvent();
    const colors = useColors();
    const fonts = useFonts();
    const logos = useLogos();
    const notificationManager = useNotificationManager();
    const confirmationsApiClient = useRef(getLibrarianApiClient(event.id)).current;
    const [, confirmationTrigger] = confirmationsState.match(extractTriggerInformationRegex) || [];
    const { isAgendaSessionRegistrationEnabledPhase2, formRegistrationForm, enabledDoubleOptIn } = useFlags();

    const { data: confirmationsConfig } = useQuery(
        ['confirmationsConfig', event.id, confirmationsState],
        () => fetchConfirmationConfigData(rootDomain, event.id, confirmationsState),
        {
            refetchOnWindowFocus: false,
        },
    );
    const isMultiSessionRsvpYes =
        event.multisession === MULTI_SESSION_VERSION_2 &&
        confirmationsState === RSVP_YES &&
        isAgendaSessionRegistrationEnabledPhase2;

    const { data, isLoading } = useQuery(
        ['confirmationStructure', event.id, confirmationsState],
        () => confirmationsApiClient.getAdminEventPageConfirmationByState(confirmationsState),
        {
            select: (response) => response?.document,
            onError: () => {
                notificationManager.showNotification({
                    id: uniqueId('CONFIRMATIONS_LOAD_'),
                    message: ERROR_MESSAGE_LOAD,
                    variation: NOTIFICATION_STATUSES.ERROR,
                });
                goToConfirmationsSettings();
            },
            staleTime: Infinity,
            cacheTime: 0,
        },
    );
    const { data: isFormRegistrationEnabled = false, isLoading: formDataLoading } = useQuery(
        ['formData', event],
        () => getFormBuilderApiClient().loadFormOwnedByEventAndByRsvpMethod(event.id, 'collect'),
        {
            select: (response) => response?.data?.forms[0]?.sessions?.enabled,
            enabled: isMultiSessionRsvpYes && formRegistrationForm,
        },
    );
    const { data: eventDoubleOptInSettings, isLoading: eventDoubleOptInLoading } = useQuery(
        ['getEventDoubleOptInSettings', event.id],
        () => getDoubleOptInApiClient().getEventDoubleOptInSettings(event.id),
        {
            refetchOnWindowFocus: false,
            enabled: confirmationsState === RSVP_YES && enabledDoubleOptIn,
        },
    );
    const isEventDoubleOptInEnabled = eventDoubleOptInSettings?.data?.isDoubleOptInEnabled ?? false;
    const isDOIComponentNeeded = confirmationsState === RSVP_YES && isEventDoubleOptInEnabled;

    const touchpointData = useMemo(
        () => ({
            event: {
                id: event.id,
                title: event.title,
                confirmationsConfig,
                multisession: event.multisession,
                touchpoint: CONFIRMATIONS,
                isFormRegistrationEnabled,
            },
            theme: {
                colors: colors.sort(byId).map(({ value }) => value),
                availableFonts: fonts.sort(byId).map(({ value }) => value),
                logos: logos.reduce((acc, { id, ...logo }) => ({ ...acc, [id]: logo }), {}),
            },
        }),
        [event.id, event.title, colors, fonts, logos, confirmationsConfig, isFormRegistrationEnabled],
    );

    const getInitialTree = () => {
        // If we have saved confirmations data, use it
        if (data) {
            // For RSVP Yes, we may need to adjust visibility of certain components
            if (confirmationsState === RSVP_YES) {
                // Clone the saved data to avoid mutating the original
                const rsvpYesData = cloneDeep(data);
                let hasDOIMessage = false;
                Object.keys(rsvpYesData).forEach((componentId) => {
                    // Adjust Session Summary visibility
                    if (isMultiSessionRsvpYes && rsvpYesData[componentId].type === 'SessionsSummary') {
                        rsvpYesData[componentId].props.meta.disabled = !isFormRegistrationEnabled;
                        rsvpYesData[componentId].props.meta.showToolTip = !isFormRegistrationEnabled;
                        rsvpYesData[componentId].props.meta.visible = isFormRegistrationEnabled;
                    }
                    // Adjust Double Opt-In message visibility
                    if (rsvpYesData[componentId].props.meta.displayName === RSVP_YES_DOI_MESSAGE_DISPLAY_NAME) {
                        rsvpYesData[componentId].props.meta.visible = isEventDoubleOptInEnabled;
                        rsvpYesData[componentId].props.meta.disabled = !isEventDoubleOptInEnabled;
                        rsvpYesData[componentId].props.meta.showToolTip = !isEventDoubleOptInEnabled;
                        hasDOIMessage = true;
                    }
                });

                // If DOI message is not present and DOI is enabled, add it
                if (enabledDoubleOptIn && isEventDoubleOptInEnabled && !hasDOIMessage) {
                    return addDoubleOptInMessage(rsvpYesData);
                }
                return rsvpYesData;
            }

            return data;
        }

        // If no saved data, and no special components needed, return default structure
        if (!isDOIComponentNeeded && !isMultiSessionRsvpYes) {
            return confirmationsDefaultStructures[confirmationTrigger];
        }

        // Build default structure with necessary components
        const confirmationComponents = isDOIComponentNeeded
            ? addDoubleOptInMessage(confirmationsDefaultStructures[confirmationTrigger])
            : cloneDeep(confirmationsDefaultStructures[confirmationTrigger]);

        if (isMultiSessionRsvpYes) {
            const sessionSummaryChild = createSessionSummaryNode();
            if (formRegistrationForm) {
                sessionSummaryChild.props.meta.visible = isFormRegistrationEnabled;
                sessionSummaryChild.props.meta.disabled = !isFormRegistrationEnabled;
                sessionSummaryChild.props.meta.showToolTip = !isFormRegistrationEnabled;
                sessionSummaryChild.props.meta.toolTipMessage = ENABLE_SESSIONS_FORM_MESSAGE;
                confirmationComponents[sessionSummaryChild.id] = sessionSummaryChild;
            }

            Object.keys(confirmationComponents).forEach((componentId) => {
                const component = confirmationComponents[componentId];
                if (component.type === 'Button') {
                    component.props.meta = AGENDA_BUTTON_META;
                }
                if (
                    formRegistrationForm &&
                    component.type === 'Modal' &&
                    Array.isArray(component.childNodes) &&
                    !component.childNodes.includes(sessionSummaryChild.id)
                ) {
                    const insertIndex = Math.max(component.childNodes.length - 1, 0);
                    component.childNodes.splice(insertIndex, 0, sessionSummaryChild.id);
                }
                if (component.type === 'CalendarAttachment') {
                    component.props.meta.visible = !isFormRegistrationEnabled;
                }
            });
        }
        // Return the constructed default structure
        return confirmationComponents;
    };

    const initialTree = useMemo(() => {
        if (isLoading || formDataLoading || eventDoubleOptInLoading) return {};
        rerender();
        return getInitialTree();
    }, [isLoading, formDataLoading, eventDoubleOptInLoading, isFormRegistrationEnabled]);
    if (isLoading || formDataLoading || eventDoubleOptInLoading) {
        return (
            <LoadingSpinnerContainer>
                <SuiSpinner loading size="large" aria-label="loading" />
            </LoadingSpinnerContainer>
        );
    }

    return (
        <TouchpointDataProvider event={touchpointData.event} theme={touchpointData.theme}>
            <ModalManager>
                <SmoothPropertyAnimation />
                <GenericConfirmation
                    goToConfirmationsSettings={goToConfirmationsSettings}
                    initialTree={initialTree}
                    confirmationsState={confirmationsState}
                />
            </ModalManager>
        </TouchpointDataProvider>
    );
}

ConfirmationsTouchpoint.propTypes = {
    confirmationsState: PropTypes.string.isRequired,
    goToConfirmationsSettings: PropTypes.func.isRequired,
};
